import React, { useState, useRef, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Loader2, Shield, AlertCircle, Smartphone, KeyRound } from 'lucide-react'
import { verify2FAOTP, send2FASMS, verifyBackupCode } from '@/lib/twoFactorService'
import { AdminUser } from '@/lib/authService'
import { supabase } from '@/lib/supabase'

interface Verify2FAProps {
  twoFactorToken: string
  onVerified: (user: AdminUser) => void
  onBack: () => void
}

export default function Verify2FA({ twoFactorToken, onVerified, onBack }: Verify2FAProps) {
  const [code, setCode] = useState(['', '', '', '', '', ''])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [resendLoading, setResendLoading] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)
  const [showBackupCode, setShowBackupCode] = useState(false)
  const [backupCode, setBackupCode] = useState('')
  const [user, setUser] = useState<any>(null)

  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  useEffect(() => {
    // Get user info from Supabase session
    async function getUserFromSession() {
      const { data: { user } } = await supabase.auth.getUser()
      if (user) {
        fetchUserDetails(user.id)
      }
    }

    getUserFromSession()

    // Focus first input
    inputRefs.current[0]?.focus()
  }, [twoFactorToken])

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [resendCooldown])

  async function fetchUserDetails(userId: string) {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single()

      if (!error && data) {
        setUser(data)

        // Auto-send SMS if 2FA method is SMS
        if (data.two_factor_method === 'sms' && data.phone) {
          // Send SMS directly with the phone from data (don't rely on state)
          await sendInitialSMS(userId, data.phone)
        }
      }
    } catch (error) {
      console.error('Error fetching user:', error)
    }
  }

  async function sendInitialSMS(userId: string, phone: string) {
    try {
      const result = await send2FASMS(userId, phone)
      if (!result.success) {
        setError(result.error || 'Failed to send verification code')
      } else {
        setResendCooldown(60) // Start cooldown after successful send
      }
    } catch (error) {
      console.error('Initial SMS send error:', error)
      setError('Failed to send verification code')
    }
  }

  function handleCodeChange(index: number, value: string) {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return

    const newCode = [...code]
    newCode[index] = value
    setCode(newCode)

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }

    // Auto-submit when all 6 digits are entered
    if (value && index === 5 && newCode.every((digit) => digit)) {
      handleVerify(newCode.join(''))
    }
  }

  function handleKeyDown(index: number, e: React.KeyboardEvent) {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      // Focus previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus()
    }
  }

  function handlePaste(e: React.ClipboardEvent) {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').slice(0, 6)
    const digits = pastedData.split('').filter((char) => /^\d$/.test(char))

    if (digits.length === 6) {
      setCode(digits)
      inputRefs.current[5]?.focus()
      // Auto-submit pasted code
      handleVerify(digits.join(''))
    }
  }

  async function handleVerify(codeString?: string) {
    const verificationCode = codeString || code.join('')
    if (verificationCode.length !== 6) {
      setError('Please enter all 6 digits')
      return
    }

    setError('')
    setLoading(true)

    try {
      // Get current Supabase session user
      const { data: { user: authUser } } = await supabase.auth.getUser()
      if (!authUser) {
        setError('Invalid verification session')
        return
      }

      // Verify the 2FA code
      const result = await verify2FAOTP(authUser.id, verificationCode)

      if (!result.success) {
        setError(result.error || 'Invalid code')
        setCode(['', '', '', '', '', ''])
        inputRefs.current[0]?.focus()
        return
      }

      // Get full user details
      const { data: userData, error: userError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', authUser.id)
        .single()

      if (userError || !userData) {
        setError('Failed to load user details')
        return
      }

      const adminUser: AdminUser = {
        id: userData.id,
        email: userData.email,
        firstName: userData.first_name,
        lastName: userData.last_name,
        role: userData.role,
        adminLevel: userData.admin_level || 'none',
        churchId: userData.church_id,
        phone: userData.phone,
        twoFactorEnabled: userData.two_factor_enabled,
        twoFactorMethod: userData.two_factor_method
      }

      onVerified(adminUser)
    } catch (error: any) {
      console.error('2FA verification error:', error)
      setError('Verification failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  async function handleResendCode() {
    if (resendCooldown > 0) return

    if (!user || !user.phone) {
      setError('No phone number on file. Please contact support.')
      return
    }

    setResendLoading(true)
    setError('')

    try {
      // Get current Supabase session user
      const { data: { user: authUser } } = await supabase.auth.getUser()
      if (!authUser) {
        setError('Invalid verification session')
        return
      }

      const result = await send2FASMS(authUser.id, user.phone)

      if (!result.success) {
        setError(result.error || 'Failed to send code')
        return
      }

      setResendCooldown(60) // 60 second cooldown
    } catch (error: any) {
      console.error('Resend error:', error)
      setError('Failed to resend code')
    } finally {
      setResendLoading(false)
    }
  }

  async function handleBackupCodeVerify() {
    if (!backupCode) {
      setError('Please enter a backup code')
      return
    }

    setError('')
    setLoading(true)

    try {
      // Get current Supabase session user
      const { data: { user: authUser } } = await supabase.auth.getUser()
      if (!authUser) {
        setError('Invalid verification session')
        return
      }

      const result = await verifyBackupCode(authUser.id, backupCode)

      if (!result.success) {
        setError(result.error || 'Invalid backup code')
        setBackupCode('')
        return
      }

      // Get user details
      const { data: userData, error: userError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', authUser.id)
        .single()

      if (userError || !userData) {
        setError('Failed to load user details')
        return
      }

      const adminUser: AdminUser = {
        id: userData.id,
        email: userData.email,
        firstName: userData.first_name,
        lastName: userData.last_name,
        role: userData.role,
        adminLevel: userData.admin_level || 'none',
        churchId: userData.church_id,
        phone: userData.phone,
        twoFactorEnabled: userData.two_factor_enabled,
        twoFactorMethod: userData.two_factor_method
      }

      onVerified(adminUser)
    } catch (error: any) {
      console.error('Backup code verification error:', error)
      setError('Verification failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (showBackupCode) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md shadow-xl border">
          <CardHeader className="space-y-1">
            <div className="flex items-center justify-center mb-4">
              <div className="p-3 rounded-full bg-primary/10">
                <KeyRound className="h-8 w-8 text-primary" />
              </div>
            </div>
            <CardTitle className="text-2xl text-center">Use Backup Code</CardTitle>
            <CardDescription className="text-center">
              Enter one of your backup recovery codes
            </CardDescription>
          </CardHeader>

          <CardContent className="pt-6">
            {error && (
              <Alert className="mb-4 bg-red-50 border-red-200">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800">{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-4">
              <Input
                type="text"
                value={backupCode}
                onChange={(e) => setBackupCode(e.target.value.toUpperCase())}
                placeholder="XXXXXXXX"
                className="text-center text-lg tracking-wider font-mono"
                maxLength={8}
                disabled={loading}
              />

              <Button
                onClick={handleBackupCodeVerify}
                disabled={loading}
                className="w-full"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify Backup Code'
                )}
              </Button>

              <Button variant="outline" onClick={() => setShowBackupCode(false)} className="w-full">
                Back to 2FA Code
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md shadow-xl border">
        <CardHeader className="space-y-1">
          <div className="flex items-center justify-center mb-4">
            <div className="p-3 rounded-full bg-primary/10">
              <Smartphone className="h-8 w-8 text-primary" />
            </div>
          </div>
          <CardTitle className="text-2xl text-center">Two-Factor Authentication</CardTitle>
          <CardDescription className="text-center">
            {user?.two_factor_method === 'sms'
              ? 'Enter the code sent to your phone'
              : 'Enter the code from your authenticator app'}
          </CardDescription>
        </CardHeader>

        <CardContent className="pt-6">
          {error && (
            <Alert className="mb-4 bg-red-50 border-red-200">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800">{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-6">
            <div className="flex justify-center gap-2" onPaste={handlePaste}>
              {code.map((digit, index) => (
                <Input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className="w-12 h-14 text-center text-2xl font-bold"
                  disabled={loading}
                />
              ))}
            </div>

            <Button
              onClick={() => handleVerify()}
              disabled={loading || code.some((d) => !d)}
              className="w-full"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Verify Code'
              )}
            </Button>

            {user?.two_factor_method === 'sms' && (
              <div className="text-center">
                <Button
                  variant="link"
                  onClick={handleResendCode}
                  disabled={resendLoading || resendCooldown > 0}
                  className="text-primary hover:text-primary/80"
                >
                  {resendLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : resendCooldown > 0 ? (
                    `Resend code in ${resendCooldown}s`
                  ) : (
                    'Resend Code'
                  )}
                </Button>
              </div>
            )}

            <div className="text-center space-y-2">
              <Button variant="link" onClick={() => setShowBackupCode(true)} className="text-sm">
                Use backup code instead
              </Button>

              <Button variant="link" onClick={onBack} className="text-sm text-gray-600">
                Back to login
              </Button>
            </div>
          </div>

          <div className="mt-6 text-center text-xs text-muted-foreground">
            <p>Code valid for 5 minutes</p>
            {user?.phone && <p className="mt-1">Sent to {user.phone.replace(/(\d{3})\d{4}(\d{3})/, '$1****$2')}</p>}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
